// ============================================================
// PieceEditor — l'écran de chiffrage d'une pièce, EN DEUX COLONNES.
// ------------------------------------------------------------
// Aucun calcul ici. La synthèse affiche ce que `computePiece` renvoie,
// et c'est la MÊME fonction que celle du serveur.
//
// ══════════════════════════════════════════════════════════════
// PROPOSÉ ET RETENU NE PARTAGENT JAMAIS LA MÊME COLONNE.
// ══════════════════════════════════════════════════════════════
//
// C'est l'invariant du projet rendu GÉOMÉTRIQUE, et ce n'est pas une
// préférence visuelle. Tout ce fichier reposait sur une pile verticale :
// la lecture de l'agent, puis la valeur retenue, puis la gamme proposée,
// puis les temps validés — les uns sous les autres, dans la même
// colonne, avec les mêmes cadres. À l'écran, plus rien ne distinguait ce
// que le programme PROPOSE de ce que l'homme RETIENT.
//
// C'est exactement ce que l'application AGC faisait mieux, pour une
// raison de fond et non de goût : à gauche tout le travail de l'IA, à
// droite les valeurs retenues.
//
//   GAUCHE — ce que le programme propose      DROITE — ce qu'on retient
//   ────────────────────────────────────      ─────────────────────────
//   la géométrie déduite du plan              la matière et le transport
//   les usinages relevés, les tolérances      la forme et les cotes du brut
//   la GAMME proposée et ses justifications   les trois temps retenus
//                                             le prix
//
// L'ordre vertical est celui du RDV : le plan (au-dessus, dans
// `DevisPage`), le chat, l'analyse, la justification du prix, la
// matière, les temps, le prix.
// ============================================================

import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import NumberField from '../../ui/NumberField.jsx';
import { Select, Textarea } from '../../ui/TextField.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Synthese from './Synthese.jsx';
import PrixCompact from './PrixCompact.jsx';
import GammeBloc from './GammeBloc.jsx';
import RevisionGamme from './RevisionGamme.jsx';
import PreChiffrage from './PreChiffrage.jsx';
import CotesLues from './CotesLues.jsx';
import SuggestionsHistorique from './SuggestionsHistorique.jsx';
import EncartPrevaut from './EncartPrevaut.jsx';
import { TauxBloc } from './TauxBloc.jsx';
import QuantityTiers from './QuantityTiers.jsx';
import SubcontractingTable from './SubcontractingTable.jsx';
import { ROUNDING_RULES, ROUNDING_LABELS } from '@shared/pricing.js';
import Barriere from '../../ui/Barriere.jsx';
import { estAChiffrer } from './BandePieces.jsx';
import { reglagesRenseignes, resumeReglages } from './reglagesPiece.js';

export function PieceEditor({
  piece,
  result,
  tiers,
  machines,
  suppliers,
  onChange,
  onRemove,
  onDuplicate,
  disabled,
  // Provenance par champ — `{ ref: 'ai' }`. Une valeur saisie par
  // l'humain n'a PAS de pastille : l'absence de marque est
  // l'information (voir `ui/SourceDot.jsx`).
  onEstimer,
  estimating,
  onReviser = null,
  revising = false,
  devisId = null,
}) {
  const errors = result.warnings.filter((w) => w.level === 'error');
  const aChiffrer = estAChiffrer(piece);
  // Ce que le repli « Réglages » contient de particulier. Rendu ici et
  // pas dans le `summary` pour que la règle se teste sans écran.
  const reglagesPoses = reglagesRenseignes(piece);
  const resume = resumeReglages(piece);

  return (
    <Card>
      <CardHead title={piece.name || piece.ref || 'Pièce sans nom'}>
        {/* ── LE MÊME MOT QUE LA BANDE, ET QUE LE BOUTON ──
            Une pièce fraîchement lue n'a pas encore de temps : depuis le
            27 août c'est l'état NORMAL, pas un manque. Elle affichait
            « 1 à compléter » pendant que le bouton juste en dessous
            proposait « Chiffrer la pièce ». Trois mots pour un état, et
            aucun ne disait le geste.

            L'état vient de `etatDePiece` — la MÊME fonction que la
            bande. Deux définitions du même mot finiraient par se
            contredire à l'écran, et c'est arrivé le jour même. */}
        {aChiffrer
          ? <Badge tone="neutral">à chiffrer</Badge>
          : errors.length > 0 ? <Badge tone="danger">{errors.length} à compléter</Badge> : null}
        <Button size="sm" variant="ghost" onClick={onDuplicate} disabled={disabled}>
          Dupliquer
        </Button>
        <Button size="sm" variant="ghost" onClick={onRemove} disabled={disabled}>
          Supprimer
        </Button>
      </CardHead>

      <CardBody className="stack">
        {/* « Rattacher » a rejoint la nuance retenue (`NuanceRetenue.jsx`, 6 octobre 2026). */}

        {/* ── L'IDENTITÉ DE LA PIÈCE EST MONTÉE À CÔTÉ DU PLAN ──
            Désignation, référence, quantité, nature et la question
            « comment usineriez-vous cette pièce ? » vivent désormais
            dans `FichePiece`, contre le dessin. Les garder ici en
            ferait un second endroit où saisir les mêmes valeurs — la
            question « lequel gagne ? » qu'on passe la session à
            supprimer. */}

        <div className="colonnes">
          {/* ── GAUCHE : CE QUE LE PROGRAMME PROPOSE ────────────── */}
          <section className="colonne colonne--propose" aria-label="Ce que le programme propose">
            <p className="colonne__titre">Ce que le programme a lu du plan</p>

            {/* ── CE QUE L'HISTORIQUE DIT, À CÔTÉ DE LA LECTURE ──
                La couche 2 rendue visible. En tête parce qu'elle porte
                sur la lecture : on regarde ce que d'autres pièces ont
                appris avant les cotes. Rien ne s'applique seul (§2bis). */}
            <Barriere zone="Historique">
              <SuggestionsHistorique devisId={devisId} piece={piece}
                disabled={disabled} onChange={onChange} />
            </Barriere>

            {/* ── CE QUI A ÉTÉ LU, AVANT CE QUI EN EST DÉDUIT ──
                L'écran montrait le BRUT — cotes calculées, majorées,
                arrondies — et nulle part ce qui avait été LU. Un brut
                issu d'une bonne lecture et un issu d'une mauvaise se
                ressemblent exactement : « ici ça reste une boîte noire ».
                D'où l'ordre : on vérifie la lecture, puis la déduction.

                Un seul cadre depuis le 26 août — même idée en deux
                moitiés — mais DEUX barrières : les fondre ferait tomber
                la lecture avec la déduction. */}
            <div className="lecture-plan">
              <Barriere zone="Cotes lues">
                <CotesLues piece={piece} disabled={disabled} onChange={onChange} />
              </Barriere>

              <Barriere zone="Géométrie déduite du plan">
                <PreChiffrage
                  piece={piece}
                  result={result}
                  disabled={disabled}
                  onChange={onChange}
                  partie="analyse"
                />
              </Barriere>
            </div>

            {/* ── LA GAMME, du côté « proposé » — §8.1 ──
                relevé → GAMME → temps → prix. C'est LA justification du
                prix, une ligne par opération.
                Elle tient de nouveau dans une colonne : les colonnes
                « machine » et « catégorie » ont fusionné sous le
                libellé, et les deux boutons sont devenus deux icônes. */}
            {/* ── LE PRIX EN BANDEAU, AU-DESSUS DE LA GAMME — 16 sept. 2026 ──
                Il était dans une colonne à droite : 125 px de carte pour
                1 800 px de gamme, donc un grand vide. Thibaud : « il faut
                quelque chose de plus harmonieux. » En bandeau COLLANT, il
                reste sous les yeux pendant qu'on corrige un temps — c'est
                sa seule raison d'être — sans ouvrir une colonne pour lui.
                Le DÉTAIL du calcul reste en pleine largeur sous la gamme
                (décision du 21 août). */}
            {piece.is_purchase ? null : <PrixCompact piece={piece} result={result} />}

            {piece.is_purchase ? null : (
              <Barriere zone="Gamme opératoire">
                <GammeBloc
                  piece={piece}
                  machines={machines}
                  disabled={disabled}
                  onChange={onChange}
                  onEstimer={onEstimer}
                  estimating={estimating}
                />
                {/* ── ON NE CORRIGE PAS CE QU'ON NE VOIT PAS ──
                    Le champ de révision est SOUS la gamme, pas en tête :
                    corriger suppose de lire ce qu'on corrige. La
                    consigne du haut, elle, oriente le premier
                    chiffrage — deux gestes, deux places. */}
                {onReviser && (piece.gamme ?? []).length ? (
                  <RevisionGamme
                    onReviser={onReviser}
                    revising={revising}
                    disabled={disabled}
                  />
                ) : null}
              </Barriere>
            )}
          </section>

        </div>

        {/* ── LE BLOC TEMPS EST SUPPRIMÉ — 22 août 2026 ──
            Charles, deux fois : « réestimer et valider toutes les valeurs
            IA n'ont plus rien à faire dans le programme », puis « enlève
            complètement la section temps ».

            Il a raison, et le §8.4 n'y perd rien. Cette section offrait
            de corriger un des trois totaux SANS descendre dans la gamme ;
            mais un total saisi FIGE le chiffre et se met à diverger de la
            gamme qui l'a produit — d'où le conflit « lequel garder ? »
            qu'il fallait ensuite arbitrer. La gamme est l'objet central :
            elle est la seule porte, y compris pour une pièce chiffrée à
            la main, où « Ajouter une opération » suffit.

            **Une seule porte par valeur**, appliqué aux temps.

            Ce qui vivait DANS ce bloc et n'a rien à voir avec lui — les
            deux taux et le coefficient de sécurité — remonte juste
            au-dessus du prix, avec les deux autres réglages qui le
            fabriquent. */}

        {/* ── 7. LE PRIX, PLEINE LARGEUR ──
            Il était dans la colonne de droite, et chaque ligne de calcul
            — « 32 min × 1 pc + 5 min de débit = 37 min × 80,00 €/h » —
            se cassait en huit lignes dans 425 px. Or c'est exactement le
            texte qui rend le prix vérifiable de tête : étranglé, il ne
            vérifie plus rien.
            Il est aussi la CONCLUSION de la pièce, pas un élément de la
            colonne « retenu » : sa place est sous les deux, pas dans
            l'une des deux. */}
        {/* ── AU-DESSUS DU PRIX, ET PAS EN DESSOUS — 21 août 2026 ──
            Charles : « monter le minimum de facturation et l'arrondi
            au-dessus de la barre, ça a à voir avec le devis et pas avec
            le reste. »

            Ce sont les deux réglages qui MODIFIENT le prix unitaire —
            l'arrondi commercial et le plancher de facturation. Les poser
            sous la synthèse les faisait lire comme une conséquence du
            prix alors qu'ils en sont une cause : on lisait un montant,
            puis on découvrait en dessous ce qui l'avait produit.

            `repli--champs` et non `repli` : ce repli ouvre des CHAMPS À
            REMPLIR, pas une explication. Voir la note du garde-fou plus
            bas. */}
        {/* ══════════════════════════════════════════════════════
            UN SEUL REPLI POUR TOUT CE QUI SE RÈGLE — 26 août 2026
            ══════════════════════════════════════════════════════

            Trois encarts distincts occupaient la vue sur chaque pièce :
            « ce qui prévaut sur l'estimation » (prix matière,
            transport, coefficient matière), les taux et le coefficient
            de sécurité, puis le minimum de facturation et l'arrondi.

            NEUF CHAMPS, tous facultatifs, tous dotés d'un défaut venant
            des paramètres, et touchés dans moins d'un devis sur cinq.
            Ils partagent aussi une nature : ce sont les réglages qui
            FABRIQUENT le prix, par opposition à la gamme et à la
            matière, qui le DÉCRIVENT.

            Ils restent tous là, à un clic, au-dessus du prix — parce
            qu'ils en sont la cause et non la conséquence (§8.4).

            ── CE QUI REND LE REPLI HONNÊTE ──

            On ne cache jamais un montant qui part chez le client. Le
            repli s'ouvre donc TOUT SEUL dès qu'un réglage porte une
            valeur propre à la pièce, et son intitulé NOMME lesquels.
            Fermé, il affirme quelque chose de vérifiable : « valeurs
            par défaut ». Voir `reglagesPiece.js`. */}
        <details className="repli repli--champs" open={reglagesPoses.length > 0}>
          <summary>
            Réglages de cette pièce — <span className="repli__resume">{resume}</span>
          </summary>
          <div className="stack">
            {piece.is_purchase ? null : (
              <EncartPrevaut
                piece={piece}
                onChange={onChange}
                disabled={disabled}
                mat={result?.detail?.material}
                detail={result?.detail}
              />
            )}

            {/* Les TAUX étaient enfouis dans le bloc Temps supprimé le
                22 août. On ne juge pas un prix sans savoir ce qui le
                facture, et le coefficient de sécurité — absent par
                défaut, affiché avec ce qu'il ajoute — est une décision
                par affaire (§6). */}
            {piece.is_purchase ? null : (
              <TauxBloc
                niveau="cette pièce"
                effectifs={{
                  machine: result?.detail?.work?.machine_rate,
                  humain: result?.detail?.work?.human_rate,
                }}
                disabled={disabled}
                coefficient={piece.coefficient_securite}
                onCoefficient={(v) => onChange({ coefficient_securite: v })}
              />
            )}

            {/* Les deux réglages qui MODIFIENT le prix unitaire :
                l'arrondi commercial et le plancher de facturation. */}
            <div className="grid-2">
              <NumberField
                label="Minimum de facturation"
                unit="€"
                value={piece.min_invoice}
                min={0}
                disabled={disabled}
                onChange={(v) => onChange({ min_invoice: v })}
              />
              <Select
                label="Arrondi"
                value={piece.rounding_rule}
                disabled={disabled}
                options={ROUNDING_RULES.map((r) => ({ value: r, label: ROUNDING_LABELS[r] }))}
                onChange={(e) => onChange({ rounding_rule: e.target.value })}
              />
            </div>
          </div>
        </details>

        <Barriere zone="Prix">
          <div id="prix-detail"><Synthese piece={piece} result={result} /></div>
        </Barriere>


        <Barriere zone="Paliers de quantité">
          <QuantityTiers piece={piece} tiers={tiers} disabled={disabled} onChange={onChange} />
        </Barriere>

        {/* Une entrée manquante ne vaut pas zéro : elle est listée noir
            sur blanc, et elle bloque le PDF et l'envoi. */}
        {errors.length > 0 ? (
          <div className="warnings" role="alert">
            <p className="caps">À compléter avant de pouvoir envoyer ce devis</p>
            <ul>
              {errors.map((w) => (
                <li key={w.field}>{w.message}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <SubcontractingTable
          lines={piece.subcontracting ?? []}
          suppliers={suppliers ?? []}
          breakdown={result.detail.subcontracting}
          disabled={disabled}
          onChange={(subcontracting) => onChange({ subcontracting })}
        />

        {/* Deux zones de texte qui se remplissent rarement et coûtaient
            250 px sur chaque pièce. Repliées — et OUVERTES dès qu'elles
            portent quelque chose : un texte caché qui part sur le devis
            serait pire que la place qu'il prend. */}
        {/* ── UN REPLI QUI OUVRE DES CHAMPS N'EST PAS UN REPLI QUI
            OUVRE UNE EXPLICATION — 21 août 2026 ──

            Charles : « la justification de correction, les notes
            internes, le minimum de facturation et l'arrondi sont
            présentés exactement comme quand on veut plus d'explication.
            Ce n'est pas logique — mettez plutôt un + à la place de la
            flèche. »

            Il a raison : la même flèche de `<details>` servait à
            « en savoir plus » et à « voici des champs à remplir ». Deux
            gestes de nature opposée sous une seule apparence — l'un se
            lit, l'autre se remplit. `repli--champs` remplace la flèche
            par un + qui devient − à l'ouverture. */}
        <details
          className="repli repli--champs"
          open={Boolean(piece.correction_justification || piece.notes_internal)}
        >
          <summary>
            Justification de correction et notes internes
            {piece.correction_justification || piece.notes_internal ? ' — remplies' : ''}
          </summary>
          <div className="stack">
            {/* La justification d'une correction — facultative, jamais
                bloquante. Un chiffre corrigé est un point de donnée ; une
                phrase est une RÈGLE, et c'est elle que l'agent 2 relira
                dans six mois. */}
            <Textarea
              label="Pourquoi avez-vous corrigé ?"
              rows={2}
              value={piece.correction_justification ?? ''}
              disabled={disabled}
              hint="Facultatif. « Une seule prise, le NLX reprend en contre-broche » vaut pour toutes les pièces comparables — un chiffre ne vaut que pour celle-ci."
              onChange={(e) => onChange({ correction_justification: e.target.value })}
            />

            <Textarea
              label="Notes internes"
              value={piece.notes_internal ?? ''}
              rows={2}
              disabled={disabled}
              hint="Ne figure pas sur le devis client."
              onChange={(e) => onChange({ notes_internal: e.target.value })}
            />
          </div>
        </details>

      </CardBody>
    </Card>
  );
}

export default PieceEditor;
